import React from 'react';
import useResponsive from '../hooks/useResponsive';
import SubjectCard from './SubjectCard';
import { roundToTwoDecimals } from '../../../utils/mathUtils';

const SubjectsGrid = ({
  selectedClass,
  selectedSemester,
  subjects,
  grades,
  isAuthenticated,
  user,
  onAddGrade,
  onEditGrade,
  onDeleteGrade,
  onRemoveSubject,
  onOpenSubjectSelection,
  onGradeMouseEnter,
  onGradeMouseLeave,
  onGradeMouseMove,
  calculateSubjectAverage,
  getSubjectGrades
}) => {
  const { isMobile, isTablet } = useResponsive();

  // Calculate semester average
  const calculateSemesterAverage = () => {
    if (subjects.length === 0) return null;
    
    const subjectAverages = subjects
      .map(subject => {
        const average = calculateSubjectAverage(subject.id);
        // Convert to number and check if it's a valid number
        const numAverage = parseFloat(average);
        return isNaN(numAverage) ? null : numAverage;
      })
      .filter(average => average !== null && average > 0);
    
    if (subjectAverages.length === 0) return null;
    
    const sum = subjectAverages.reduce((acc, avg) => acc + avg, 0);
    const finalAverage = sum / subjectAverages.length;
    
    // Double check the final result
    return isNaN(finalAverage) ? null : roundToTwoDecimals(finalAverage);
  };

  const semesterAverage = calculateSemesterAverage();

  const getGridCols = () => {
    if (isMobile) return 'grid-cols-1';
    if (isTablet) return 'grid-cols-1 sm:grid-cols-2';
    return 'grid-cols-1 lg:grid-cols-2';
  };

  if (!selectedClass || !selectedSemester) {
    return null;
  }

  return (
    <div className="row mb-4">
      <div className="col-12">
        <div className="card border-0 shadow-sm rounded-xl">
          {/* Subjects Header */}
          <div className="bg-gradient-to-r from-green-cloud to-green-cloud/80 text-white px-4 sm:px-6 py-4 rounded-t-xl">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-3 sm:space-y-0">
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-full flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-book text-white text-sm sm:text-base"></i>
                </div>
                <div>
                  <div className="flex items-center space-x-3">
                    <h3 className="text-lg sm:text-xl font-bold">Subjects</h3>
                  
                  </div>
                  <p className="text-white/80 text-sm">
                    {selectedSemester.name}
                    {semesterAverage !== null && !isNaN(semesterAverage) && (
                      <span className="ml-2 text-white/70">
                        • Overall average: {Math.round(semesterAverage * 100) / 100}
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                <div className="bg-white/20 px-3 py-1 rounded-full flex-shrink-0">
                  <span className="text-sm font-medium">
                    {subjects.length} subject{subjects.length !== 1 ? 's' : ''}
                  </span>
                </div>
                {semesterAverage !== null && !isNaN(semesterAverage) && (
                  <div className="bg-white/30 px-3 py-2 rounded-lg flex items-center space-x-2 flex-shrink-0">
                    <i className="fas fa-chart-line text-white text-sm"></i>
                    <div className="text-center">
                      <div className="text-xs text-white/80 font-medium">Semester Avg</div>
                      <div className="text-lg font-bold text-white">
                        {Math.round(semesterAverage * 100) / 100}
                      </div>
                    </div>
                  </div>
                )}
                {isAuthenticated && user && (
                  <button
                    onClick={onOpenSubjectSelection}
                    className="bg-white/20 hover:bg-white/30 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 flex items-center space-x-2 text-sm sm:text-base flex-1 sm:flex-initial justify-center"
                  >
                    <i className="fas fa-plus"></i>
                    <span>{isMobile ? 'Add' : 'Add Subjects'}</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Subjects Grid */}
          <div className="p-4 sm:p-6">
            {subjects.length > 0 ? (
              <div className={`grid ${getGridCols()} gap-4 sm:gap-6`}>
                {subjects.map((subject) => {
                  const subjectGrades = getSubjectGrades(subject.id);
                  const average = calculateSubjectAverage(subject.id);

                  return (
                    <SubjectCard
                      key={subject.id}
                      subject={subject}
                      subjectGrades={subjectGrades}
                      average={average}
                      isAuthenticated={isAuthenticated}
                      user={user}
                      onAddGrade={onAddGrade}
                      onEditGrade={onEditGrade}
                      onDeleteGrade={onDeleteGrade}
                      onRemoveSubject={onRemoveSubject}
                      onGradeMouseEnter={onGradeMouseEnter}
                      onGradeMouseLeave={onGradeMouseLeave}
                      onGradeMouseMove={onGradeMouseMove}
                      canRemoveSubject={subjects.length > 1}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-sunrise-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="fas fa-book-open text-sunrise-red text-xl sm:text-2xl"></i>
                </div>
                <h4 className="text-lg font-semibold text-grey-dusk mb-2">
                  No subjects selected
                </h4>
                <p className="text-gray-600 mb-6 text-sm sm:text-base px-4">
                  {isAuthenticated && user 
                    ? "Select subjects for this semester to start tracking your grades."
                    : "Please log in to select subjects and track your grades."
                  }
                </p>
                {isAuthenticated && user && (
                  <button
                    onClick={onOpenSubjectSelection}
                    className="bg-sunrise-red hover:bg-sunrise-red/90 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 flex items-center space-x-2 mx-auto shadow-lg hover:shadow-xl text-sm sm:text-base"
                  >
                    <i className="fas fa-plus"></i>
                    <span>Add Subjects</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubjectsGrid;